import { FormInput } from '@/domain/mitarbeiter/features/anlageassistent/types/types';
import { FristenGrund } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/fristenGrund';
import { hoursInputToMinutes } from '@/domain/mitarbeiter/features/anlageassistent/utils/hoursInputToMinutes';
import { mapAnrede } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapAnrede';
import { mapAnstellungstyp } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapAnstellungstyp';
import { mapAnzahlArbeitstage } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapAnzahlArbeitstage';
import { mapArbeitstage } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapArbeitstage';
import { mapBerechnungsgrundlageDreiMonatsDurchschnitt } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapBerechnungsgrundlageDreiMonatsdurchschnitt';
import { mapDynamischeAbwesenheitArt } from '@/domain/mitarbeiter/features/anlageassistent/utils/mappings/mapDynamischeAbwesenheitArt';
import { moneyInputToNumber } from '@/domain/mitarbeiter/features/anlageassistent/utils/moneyInputToNumber';
import { getErinnerungsZeitFromFristDatum } from '@/domain/mitarbeiter/shared/utils/getErinerungsZeitFromFristDatum/getErinnerungsZeitFromFristDatum';
import {
  Mitarbeiter_BerechnungsartMonat,
  Mitarbeiter_Bezahlungsart,
  Mitarbeiter_MitarbeiterCreateInput,
  Mitarbeiter_StundenBewertung,
  Mitarbeiter_Stundenpensum,
} from '@/gql/graphql';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';

// eslint-disable-next-line max-lines-per-function, complexity
export function mapFormToInput({
  values,
  t,
}: {
  values: FormInput;
  t: TFunction;
}): Mitarbeiter_MitarbeiterCreateInput {
  const { person, anstellungsverhaeltnis, bezahlung, abwesenheiten } = values;
  const { persoenliches, kontakt, arbeitsbereiche } = person;
  const { anstellung, fristen } = anstellungsverhaeltnis;
  const { bezahlungsart, arbeitszeitkonto, zuschlaege } = bezahlung;
  const { allgemein: abwesenheitenAllgemein } = abwesenheiten;

  // check minimal required fields for typescript inference
  if (
    !persoenliches.anrede ||
    persoenliches.vorname.length === 0 ||
    persoenliches.nachname.length === 0 ||
    arbeitsbereiche.primaererArbeitsbereich.length === 0 ||
    !anstellung.eintrittsdatum ||
    bezahlungsart.art === undefined
  ) {
    throw new Error('Form validation failed');
  }

  return {
    person: {
      persoenliches: {
        anrede: mapAnrede(persoenliches.anrede),
        vorname: persoenliches.vorname,
        nachname: persoenliches.nachname,
        rufname: persoenliches.rufname,
        personalnummer: persoenliches.personalnummer,
        e2nId: persoenliches.e2nId,
      },
      kontakt: {
        email: kontakt.email,
        mobilnummer: kontakt.mobilnummer,
      },
      arbeitsbereiche: {
        primaererArbeitsbereich: arbeitsbereiche.primaererArbeitsbereich,
        sekundaereArbeitsbereiche: arbeitsbereiche.sekundaereArbeitsbereiche,
        erfahrungslevel: arbeitsbereiche.erfahrungslevel,
      },
    },
    anstellungsverhaeltnis: {
      anstellung: {
        anstellungstyp: mapAnstellungstyp(anstellung.anstellungstyp),
        eintrittsdatum: dayjs(anstellung.eintrittsdatum).format('YYYY-MM-DD'),
        befristung: anstellung.befristung
          ? dayjs(anstellung.befristung).format('YYYY-MM-DD')
          : undefined,
      },
      fristen: fristen.map((f) => ({
        fristdatum: dayjs(f.fristdatum!).format('YYYY-MM-DD'),
        grund:
          f.grund === FristenGrund.ANDERER
            ? f.andererGrund!
            : t(
                'domain.mitarbeiter.create.steps.anstellungsverhaeltnis.fristen.form.grund.options.' +
                  f.grund!,
              ),
        bemerkung: f.bemerkung,
        erinnerungsZeit: f.erinnerungsZeit.map((val) =>
          getErinnerungsZeitFromFristDatum(val, f.fristdatum!),
        ),
        erinnerungAn: f.erinnerungAn,
      })),
    },
    bezahlung: {
      bezahlungsart: {
        art:
          bezahlungsart.art === 'fixgehalt'
            ? Mitarbeiter_Bezahlungsart.FixGehalt
            : Mitarbeiter_Bezahlungsart.Stunden,
        stundenlohn: bezahlungsart.stundenlohn
          ? moneyInputToNumber(bezahlungsart.stundenlohn)
          : undefined,
        monatsgehalt: bezahlungsart.monatsgehalt
          ? moneyInputToNumber(bezahlungsart.monatsgehalt)
          : undefined,
      },
      sollstunden: {
        sollstunden: arbeitszeitkonto.gehaltsempfaenger.sollstunden,
        stundenpensum: arbeitszeitkonto.gehaltsempfaenger.stundenpensum
          ? arbeitszeitkonto.gehaltsempfaenger.stundenpensum === 'monat'
            ? Mitarbeiter_Stundenpensum.Monat
            : Mitarbeiter_Stundenpensum.Woche
          : undefined,
        berechnungsartMonat: arbeitszeitkonto.gehaltsempfaenger
          .berechnungsartMonat
          ? arbeitszeitkonto.gehaltsempfaenger.berechnungsartMonat ===
            'durchschnitt'
            ? Mitarbeiter_BerechnungsartMonat.Durchschnitt
            : Mitarbeiter_BerechnungsartMonat.Exakt
          : undefined,
        monatsstunden: hoursInputToMinutes(
          arbeitszeitkonto.gehaltsempfaenger.monatsstunden,
        ),
        arbeitstage:
          arbeitszeitkonto.gehaltsempfaenger.arbeitstage.length > 0
            ? arbeitszeitkonto.gehaltsempfaenger.arbeitstage.map((a) =>
                mapArbeitstage(a),
              )
            : undefined,
        tagessoll: hoursInputToMinutes(
          arbeitszeitkonto.gehaltsempfaenger.tagessoll,
        ),
        wochenstunden: hoursInputToMinutes(
          arbeitszeitkonto.gehaltsempfaenger.wochenstunden,
        ),
        unbezahlteUeberstundenMonat: hoursInputToMinutes(
          arbeitszeitkonto.gehaltsempfaenger.unbezahlteUeberstundenMonat,
        ),
        unbezahlteUeberstundenWoche: hoursInputToMinutes(
          arbeitszeitkonto.gehaltsempfaenger.unbezahlteUeberstundenWoche,
        ),
      },
      zuschlaege: zuschlaege.zuschlaege
        ? {
            zuschlagsgruppe: zuschlaege.zuschlagsgruppe,
            pauschaleZuschlaege: zuschlaege.pauschaleZuschlaege
              ? moneyInputToNumber(zuschlaege.pauschaleZuschlaege)
              : undefined,
            uebertariflicheZahlung: zuschlaege.uebertariflicheZahlung
              ? moneyInputToNumber(zuschlaege.uebertariflicheZahlung)
              : undefined,
          }
        : undefined,
    },
    abwesenheiten: {
      allgemein: {
        anzahlUrlaubstage: abwesenheitenAllgemein.anzahlUrlaubstage
          ? parseFloat(abwesenheitenAllgemein.anzahlUrlaubstage)
          : undefined,
        anzahlArbeitstage: mapAnzahlArbeitstage(
          abwesenheitenAllgemein.anzahlArbeitstage,
        ),
        stundenBewertung:
          abwesenheitenAllgemein.stundenBewertung === 'fest'
            ? Mitarbeiter_StundenBewertung.Fest
            : Mitarbeiter_StundenBewertung.Dynamisch,
        stunden: hoursInputToMinutes(abwesenheitenAllgemein.stunden),
        abwesenheitszeitBegrenzen:
          abwesenheitenAllgemein.dynamisch_wochenschnitt_limitieren,
        dynamischeAbwesenheitsArt: abwesenheitenAllgemein.dynamisch
          ? mapDynamischeAbwesenheitArt(abwesenheitenAllgemein.dynamisch)
          : undefined,
        berechnungsgrundlageDreiMonatsdurchschnitt:
          abwesenheitenAllgemein.dynamisch_drei_monatsschnitt_berechnungsgrundlage
            ? mapBerechnungsgrundlageDreiMonatsDurchschnitt(
                abwesenheitenAllgemein.dynamisch_drei_monatsschnitt_berechnungsgrundlage,
              )
            : undefined,
      },
    },
    speicheroptionen: {
      personalzugang: values.speicheroptionen.personalzugang,
    },
  };
}
